import type { UseSeoMetaInput } from '@unhead/vue'
import type {
  Article,
  Configuration,
  ContentBlock,
  Image,
  Menu,
  NextEventBanner,
  ScheduleWeek,
  Season,
  Sponsor,
} from '~/types'
import { MenuType as MenuTypeEnum } from '~/enums/menu-type'

export const useConfigStore = defineStore('config', () => {
  const { search } = useConfigurationApi()
  const config = ref<Maybe<Configuration.Model>>(null)
  const isLoading = ref<boolean>(false)

  const { handleException } = useHandleException()

  // Getters
  const menus = computed<Menu.Model[]>(() => config.value?.menus ?? [])
  const headerMenu = computed<Maybe<Menu.Model>>(() => menus.value.find(menu => menu?.slug === MenuTypeEnum.Header) ?? null)
  const footerMenu = computed<Maybe<Menu.Model>>(() => menus.value.find(menu => menu?.slug === MenuTypeEnum.Footer) ?? null)
  const footerSocialsMenu = computed<Maybe<Menu.Model>>(() => menus.value.find(menu => menu?.slug === MenuTypeEnum.FooterSocials) ?? null)
  const footerTeamColumnsMenu = computed<Maybe<Menu.Model>>(() => menus.value.find(menu => menu?.slug === MenuTypeEnum.FooterTeamColumns) ?? null)
  const websiteDescription = computed<Maybe<string>>(() => config.value?.websiteDescription ?? null)
  const websiteMainImage = computed<Maybe<Image>>(() => config.value?.websiteMainImage ?? null)
  const websiteMetaImageUrl = computed<Maybe<string>>(() => config.value?.websiteMetaImageUrl ?? null)
  const websiteTitle = computed<string>(() => config.value?.websiteSeoTitle ?? '')
  const defaultTimezone = computed<string>(() => config.value?.timezone ?? 'UTC')
  const defaultSeason = computed<Maybe<Season.Model>>(() => config.value?.season ?? null)
  const defaultSeasonId = computed<Maybe<number>>(() => defaultSeason.value?.id ?? null)
  const defaultWeek = computed<Maybe<ScheduleWeek.Model>>(() => config.value?.scheduleWeek ?? null)
  const defaultWeekId = computed<Maybe<number>>(() => defaultWeek.value?.id ?? null)
  const nextEventBanners = computed<NextEventBanner.Model[]>(() => config.value?.nextEventBanners || [])
  const facebookPixelId = computed<Maybe<string>>(() => config.value?.analytics?.facebookPixelId ?? null)
  const googleTagManagerId = computed<Maybe<string>>(() => config.value?.analytics?.googleTagManagerId ?? null)
  const googleAdManagerId = computed<Maybe<string>>(() => config.value?.analytics?.googleAdManagerId ?? null)
  const googleAdManagerCode = computed<Maybe<string>>(() => config.value?.analytics?.googleAdManagerCode ?? null)
  const googleAnalyticsId = computed<Maybe<string>>(() => config.value?.analytics?.googleAnalyticsId ?? null)
  const slotContentBlocks = computed<ContentBlock.Model[]>(() => headerMenu.value?.slotContentBlocks ?? [])
  const hasSlotContentBlocks = computed<boolean>(() => slotContentBlocks.value.length > 0)
  const tbdTeamId = computed<Maybe<number>>(() => config.value?.tbdTeamId ?? null)
  const featuredCategory = computed<Maybe<Article.Category>>(() => config.value?.featuredCategory ?? null)
  const headlineCategory = computed<Maybe<Article.Category>>(() => config.value?.headlineCategory ?? null)
  const sponsors = computed<Sponsor.Model[]>(() => {
    const contentBlocks: ContentBlock.Model<Sponsor.Model>[] = config.value?.sponsors ?? []
    return contentBlocks.map(item => item.data)
  })

  const sportRadarCurrentSeason = computed<Maybe<Season.Model>>(() => config.value?.sportradarCurrentSeason ?? null)
  const sportRadarCurrentSeasonId = computed<Maybe<number>>(() => sportRadarCurrentSeason.value?.id ?? null)
  const sportRadarCurrentWeek = computed<Maybe<ScheduleWeek.Model>>(() => config.value?.sportradarCurrentWeek ?? null)
  const sportRadarCurrentWeekId = computed<Maybe<number>>(() => sportRadarCurrentWeek.value?.id ?? null)

  const seoMeta = ref<UseSeoMetaInput>({
    title: websiteTitle,
    ogTitle: websiteTitle,
    description: websiteDescription,
    ogDescription: websiteDescription,
    ogImage: websiteMetaImageUrl,
    ogImageSecureUrl: websiteMetaImageUrl,
    ogLocale: 'en_US',
  })

  // Actions
  const fetchData = async (): Promise<Configuration.Model | undefined> => {
    isLoading.value = true
    try {
      const { data } = await search()

      return data
    }
    catch (ex: unknown) {
      handleException(ex)
      throw ex
    }
    finally {
      isLoading.value = false
    }
  }

  const set = (payload: MaybeRef<Maybe<Configuration.Model>>): void => {
    config.value = unref(payload)
  }

  return {
    config,
    defaultSeason,
    defaultSeasonId,
    defaultWeek,
    defaultWeekId,
    defaultTimezone,
    facebookPixelId,
    featuredCategory,
    footerMenu,
    footerSocialsMenu,
    footerTeamColumnsMenu,
    googleAdManagerCode,
    googleAdManagerId,
    googleAnalyticsId,
    googleTagManagerId,
    headlineCategory,
    hasSlotContentBlocks,
    headerMenu,
    nextEventBanners,
    seoMeta,
    slotContentBlocks,
    sponsors,
    sportRadarCurrentSeason,
    sportRadarCurrentSeasonId,
    sportRadarCurrentWeek,
    sportRadarCurrentWeekId,
    tbdTeamId,
    websiteDescription,
    websiteMainImage,
    websiteMetaImageUrl,
    websiteTitle,

    fetchData,
    set,
  }
})
